import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import api from "../../lib/api";
import PublicShell from "../../components/PublicShell";
import { useBrand } from "../../lib/brand";
import { formatMoney } from "../../lib/auth";
import { CheckCircle2, Clock, XCircle, Info, ExternalLink } from "lucide-react";
import { Button } from "../../components/ui/button";

const statusBadge = {
  hold: { icon: <Clock className="w-4 h-4" />, label: "Aguardando pagamento", color: "bg-amber-50 text-amber-800 border-amber-200" },
  pending: { icon: <Clock className="w-4 h-4" />, label: "Pagamento em andamento", color: "bg-amber-50 text-amber-800 border-amber-200" },
  paid: { icon: <CheckCircle2 className="w-4 h-4" />, label: "Pagamento aprovado", color: "bg-emerald-50 text-emerald-800 border-emerald-200" },
  confirmed: { icon: <CheckCircle2 className="w-4 h-4" />, label: "Reserva confirmada", color: "bg-emerald-50 text-emerald-800 border-emerald-200" },
  cancelled: { icon: <XCircle className="w-4 h-4" />, label: "Cancelada", color: "bg-slate-50 text-slate-700 border-slate-200" },
  refunded: { icon: <Info className="w-4 h-4" />, label: "Reembolsada", color: "bg-slate-50 text-slate-700 border-slate-200" },
};

export default function GuestReservation() {
  const { token } = useParams();
  const [params] = useSearchParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [retryingCheckout, setRetryingCheckout] = useState(false);
  const brand = useBrand();

  async function load() {
    try {
      const r = await api.get(`/public/reservations/by-token/${token}`);
      setData(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Reserva não encontrada");
    }
  }

  useEffect(() => {
    load();
    const t = setInterval(load, 10000); // refresh
    return () => clearInterval(t);
  }, [token]);

  if (error)
    return (
      <PublicShell>
        <div className="max-w-3xl mx-auto py-32 px-6 text-center">
          <h1 className="font-display text-4xl text-[#0AA89C]">Reserva não encontrada</h1>
          <p className="text-slate-500 mt-3">{error}</p>
        </div>
      </PublicShell>
    );

  if (!data) return <PublicShell><div className="max-w-3xl mx-auto py-32 px-6" /></PublicShell>;
  const { reservation, house } = data;
  const status = statusBadge[reservation.status] || statusBadge.pending;

  async function retryCheckout() {
    setRetryingCheckout(true);
    try {
      const { data: pay } = await api.post("/public/payments/checkout", {
        reservation_id: reservation.id,
      });
      if (pay.init_point) window.location.href = pay.init_point;
    } catch (e) {
      if (e?.response?.status === 410) {
        // pix_only mode
        window.location.href = `/reserva/${reservation.guest_link_token}/pix`;
        return;
      }
      alert(e?.response?.data?.detail || "Falha ao gerar link de pagamento");
    } finally {
      setRetryingCheckout(false);
    }
  }

  return (
    <PublicShell>
      <section className="max-w-3xl mx-auto px-6 lg:px-10 py-16">
        <div className={`inline-flex items-center gap-2 border rounded-full px-4 py-1.5 text-sm ${status.color}`} data-testid="reservation-status">
          {status.icon}
          {status.label}
        </div>
        <h1 className="font-display text-4xl md:text-5xl text-[#0AA89C] mt-6">Sua reserva {reservation.code}</h1>
        <p className="text-slate-600 mt-2">{house.name} — {house.city} / {house.state}</p>

        {params.get("status") === "success" && reservation.status === "pending" && (
          <div className="mt-6 border border-[#3B9FD4]/40 bg-[#3B9FD4]/10 text-[#22302E] rounded-md p-4 text-sm">
            Recebemos a confirmação do retorno do pagamento. A confirmação definitiva da reserva depende da
            notificação oficial do Mercado Pago e ocorre em até alguns minutos. Esta página é atualizada
            automaticamente.
          </div>
        )}

        <div className="mt-10 bg-white border border-slate-200 rounded-lg divide-y divide-slate-200">
          <Row label="Check-in" value={`${reservation.check_in} — ${house.check_in_time || "15:00"}`} />
          <Row label="Check-out" value={`${reservation.check_out} — ${house.check_out_time || "11:00"}`} />
          <Row label="Hóspedes" value={reservation.guests} />
          <Row label="Noites" value={reservation.nights} />
          <Row label="Titular" value={reservation.guest.name} />
          <Row label="E-mail" value={reservation.guest.email} />
          <Row label="Telefone" value={reservation.guest.phone} />
          <Row label="Diárias" value={formatMoney(reservation.base_total)} />
          <Row label="Taxa de limpeza" value={formatMoney(reservation.cleaning_fee)} />
          {reservation.extras > 0 && <Row label="Extras" value={formatMoney(reservation.extras)} />}
          {reservation.discount > 0 && <Row label="Desconto" value={`− ${formatMoney(reservation.discount)}`} />}
          <Row label="Total" value={<span className="font-semibold">{formatMoney(reservation.total)}</span>} />
        </div>

        {(reservation.status === "hold" || reservation.status === "pending") && (
          <div className="mt-8 flex flex-wrap gap-3">
            <Button
              data-testid="retry-checkout"
              className="bg-[#0AA89C] hover:bg-[#066B63] text-white"
              onClick={retryCheckout}
              disabled={retryingCheckout}
            >
              {retryingCheckout ? "Redirecionando…" : "Pagar via Mercado Pago"}
              <ExternalLink className="w-4 h-4 ml-2" />
            </Button>
            <a href={`https://wa.me/${brand?.contact_whatsapp || ""}`} className="text-sm text-[#0AA89C] underline self-center">
              Falar com o time
            </a>
          </div>
        )}

        {(reservation.status === "paid" || reservation.status === "confirmed") && (
          <div className="mt-10 bg-[#0AA89C] text-white p-8 rounded-lg">
            <h2 className="font-display text-2xl">Instruções de chegada</h2>
            <p className="text-slate-200 text-sm mt-2 leading-relaxed">
              Endereço completo, código do portão e detalhes finais serão enviados 24h antes do check-in.
              Fique atento ao seu e-mail e WhatsApp.
            </p>
            {house.address && <p className="mt-4 text-sm">Endereço: {house.address}</p>}
          </div>
        )}
      </section>
    </PublicShell>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between px-5 py-3 text-sm">
      <span className="text-slate-500">{label}</span>
      <span className="text-slate-800">{value}</span>
    </div>
  );
}
